import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/use-auth';
import { useClaimLinks } from './hooks/use-claim-links';
import { STEALTH_MODE, MAINTENANCE_MODE } from './lib/config';

// Layouts
import { AppLayout } from './components/layout/app-layout';
import { LandingLayout } from './components/layout/landing-layout';

// Pages
import { LandingPage } from './pages/landing';
import MaintenancePage from './pages/maintenance';
import { PricingPage } from './pages/pricing';
import { FeaturesPage } from './pages/features';
import { AboutPage } from './pages/about';
import { LoginPage } from './pages/login';
import { ForgotPasswordPage } from './pages/forgot-password';
import { ResetPasswordPage } from './pages/reset-password';
import { DashboardPage } from './pages/dashboard';
import { AnalyticsPage } from './pages/analytics';
import { LinksPage } from './pages/links';
import { LinkDetailPage } from './pages/link-detail';
import { SettingsPage } from './pages/settings';
import { NotificationsSettingsPage } from './pages/notifications-settings';
import { ApiTokensPage } from './pages/api-tokens';
import { BillingPage } from './pages/billing';
import { AdminUsersPage } from './pages/admin/users';
import { AdminUserDetailPage } from './pages/admin/user-detail';
import { AdminAccessRequestsPage } from './pages/admin/access-requests';
import { AdminLinksPage } from './pages/admin/links';
import { AdminDashboardPage } from './pages/admin/dashboard';
import { AdminAuditLogsPage } from './pages/admin/audit-logs';
import { NotFoundPage } from './pages/not-found';
import { DocsPage } from './pages/docs';
import { CampaignsPage } from './pages/campaigns';
import { IntegrationsSettingsPage } from './pages/integrations-settings';
import { SharedPage } from './pages/shared';
import { SharedDetailPage } from './pages/shared-detail';
import { SharingSettingsPage } from './pages/sharing-settings';
import { ProjectsPage } from './pages/projects';
import { ProjectDetailPage } from './pages/project-detail';
import { QRCodesPage } from './pages/qr-codes';
import { QRCodeDetailPage } from './pages/qr-code-detail';
import { PagesPage } from './pages/pages';
import { PageEditorPage } from './pages/page-editor';
import { FormsPage } from './pages/forms';
import { FormEditorPage } from './pages/form-editor';
import { LinkEditorPage } from './pages/link-editor';
import { QRCodeEditorPage } from './pages/qr-code-editor';
import { FormSubmissionsPage } from './pages/form-submissions';
import { EmailNewsletterPage } from './pages/email-newsletter';
import { DirectMailPage } from './pages/direct-mail';
import { SmsPage } from './pages/sms';
import { WorkflowsPage } from './pages/workflows';
import { ReferralsPage } from './pages/referrals';
import { TeamSettingsPage } from './pages/team-settings';
import { InvitePage } from './pages/invite';

/**
 * Check if maintenance mode bypass cookie is present
 */
function hasMaintenanceBypass(): boolean {
  if (!MAINTENANCE_MODE.enabled) return false;

  const cookies = document.cookie.split(';');
  const bypassCookie = cookies.find((cookie) =>
    cookie.trim().startsWith(`${MAINTENANCE_MODE.bypassCookieName}=`)
  );

  if (!bypassCookie) return false;

  const value = bypassCookie.split('=')[1]?.trim();
  return value === MAINTENANCE_MODE.bypassSecret;
}

// Protected route wrapper
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();
  // Automatically claim any public links the user created before signing up
  useClaimLinks();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// Admin route wrapper
function AdminRoute({ children }: { children: React.ReactNode }) {
  const { user, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (user.role !== 'admin') {
    return <Navigate to="/links" replace />;
  }

  return <>{children}</>;
}

export default function App() {
  // When maintenance mode is enabled, show maintenance page unless bypass cookie is set
  if (MAINTENANCE_MODE.enabled && !hasMaintenanceBypass()) {
    return (
      <Routes>
        <Route path="*" element={<MaintenancePage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      {/* Public landing routes */}
      <Route element={<LandingLayout />}>
        <Route path="/" element={<LandingPage />} />
        {/* In stealth mode, redirect feature pages to home */}
        <Route
          path="/features"
          element={STEALTH_MODE ? <Navigate to="/" replace /> : <FeaturesPage />}
        />
        <Route
          path="/pricing"
          element={STEALTH_MODE ? <Navigate to="/" replace /> : <PricingPage />}
        />
        <Route path="/about" element={STEALTH_MODE ? <Navigate to="/" replace /> : <AboutPage />} />
      </Route>

      {/* Auth routes */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Public API documentation */}
      <Route path="/docs" element={<DocsPage />} />

      {/* Workspace invitation acceptance */}
      <Route path="/invite/:token" element={<InvitePage />} />

      {/* Protected app routes */}
      <Route
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="/dashboard" element={<DashboardPage />} />
        <Route path="/analytics" element={<AnalyticsPage />} />
        <Route path="/links" element={<LinksPage />} />
        <Route path="/links/:shortCode" element={<LinkDetailPage />} />
        <Route path="/projects" element={<ProjectsPage />} />
        <Route path="/projects/:id" element={<ProjectDetailPage />} />
        <Route path="/qr-codes" element={<QRCodesPage />} />
        <Route path="/qr-codes/:id" element={<QRCodeDetailPage />} />
        <Route path="/pages" element={<PagesPage />} />
        <Route path="/forms" element={<FormsPage />} />
        <Route path="/forms/:formId/submissions" element={<FormSubmissionsPage />} />
        <Route path="/email-newsletter" element={<EmailNewsletterPage />} />
        <Route path="/direct-mail" element={<DirectMailPage />} />
        <Route path="/sms" element={<SmsPage />} />
        <Route path="/workflows" element={<WorkflowsPage />} />
        <Route path="/campaigns" element={<CampaignsPage />} />
        <Route path="/shared" element={<SharedPage />} />
        <Route path="/shared/:shareId" element={<SharedDetailPage />} />
        <Route path="/settings" element={<SettingsPage />} />
        <Route path="/settings/notifications" element={<NotificationsSettingsPage />} />
        <Route path="/settings/integrations" element={<IntegrationsSettingsPage />} />
        <Route path="/settings/sharing" element={<SharingSettingsPage />} />
        <Route path="/settings/tokens" element={<ApiTokensPage />} />
        <Route path="/settings/billing" element={<BillingPage />} />
        <Route path="/settings/referrals" element={<ReferralsPage />} />
        <Route path="/settings/team" element={<TeamSettingsPage />} />
        {/* Also support /referrals as a shortcut */}
        <Route path="/referrals" element={<ReferralsPage />} />
      </Route>

      {/* Full-page editors (protected, no AppLayout - they have their own layout) */}
      <Route
        path="/pages/new"
        element={
          <ProtectedRoute>
            <PageEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/pages/:id/edit"
        element={
          <ProtectedRoute>
            <PageEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/new"
        element={
          <ProtectedRoute>
            <FormEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/forms/:id/edit"
        element={
          <ProtectedRoute>
            <FormEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/links/new"
        element={
          <ProtectedRoute>
            <LinkEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/links/:shortCode/edit"
        element={
          <ProtectedRoute>
            <LinkEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qr-codes/new"
        element={
          <ProtectedRoute>
            <QRCodeEditorPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/qr-codes/:id/edit"
        element={
          <ProtectedRoute>
            <QRCodeEditorPage />
          </ProtectedRoute>
        }
      />

      {/* Admin routes */}
      <Route
        path="/admin"
        element={
          <AdminRoute>
            <AppLayout />
          </AdminRoute>
        }
      >
        <Route index element={<Navigate to="/admin/dashboard" replace />} />
        <Route path="dashboard" element={<AdminDashboardPage />} />
        <Route path="links" element={<AdminLinksPage />} />
        <Route path="users" element={<AdminUsersPage />} />
        <Route path="users/:userId" element={<AdminUserDetailPage />} />
        <Route path="access-requests" element={<AdminAccessRequestsPage />} />
        <Route path="audit-logs" element={<AdminAuditLogsPage />} />
      </Route>

      {/* 404 Page - uses landing layout for consistent navigation */}
      <Route element={<LandingLayout />}>
        <Route path="/404" element={<NotFoundPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}
